import axios from 'axios';

// Configuración del cliente API para Markezy
const apiClient = axios.create({
  baseURL: 'https://app.markezy.es/api/', // Production
  // baseURL: 'http://localhost:8000/api/', // Development
  // baseURL: 'https://markezy.test-by-neural.es/api/', // Staging
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor para añadir el token de autenticación
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Función general para hacer PATCH con refresh automático de datos
export const patchWithRefresh = async (endpoint, data, refetchFunction = null) => {
  try {
    const response = await apiClient.patch(endpoint, data);

    // Si se proporciona una función de refetch, ejecutarla después del PATCH exitoso
    if (refetchFunction && typeof refetchFunction === 'function') {
      try {
        await refetchFunction();
      } catch (refetchError) {
        console.warn('Error al hacer refetch después del PATCH:', refetchError);
        // No lanzamos el error del refetch para no interrumpir el flujo principal
      }
    }

    return response;
  } catch (error) {
    // Re-lanzar el error para que sea manejado por el componente
    throw error;
  }
};

// Función para eliminar un activo digital de un cliente
export const deleteClientDigitalAsset = async (clientId, assetId) => {
  try {
    const response = await apiClient.delete(`/clients/${clientId}/digital-assets/${assetId}/`);
    return response;
  } catch (error) {
    throw error;
  }
};

// Función para enviar mensajes con adjuntos usando multipart/form-data
export const sendMessageWithAttachments = async (
  threadId,
  content,
  attachments = [],
  messageType = 'normal',
  actionAssignedTo = null
) => {
  try {
    const formData = new FormData();

    // Añadir datos del mensaje
    formData.append('thread', threadId);
    formData.append('content', content);

    if (messageType) {
      formData.append('message_type', messageType);
    }

    if (actionAssignedTo) {
      formData.append('action_assigned_to', actionAssignedTo);
    }

    // Añadir archivos adjuntos
    attachments.forEach((file, index) => {
      formData.append('attachments', file);
    });

    const response = await apiClient.post('/communication/messages/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return response;
  } catch (error) {
    // Manejo específico de errores de adjuntos
    if (error.response) {
      const { status, data } = error.response;

      switch (status) {
        case 400:
          if (data.detail && data.detail.includes('size')) {
            throw new Error('El archivo es demasiado grande. El tamaño máximo permitido es 50MB.');
          }
          throw new Error(data.detail || 'Error en los datos enviados');
        case 401:
          throw new Error('No estás autenticado. Por favor, inicia sesión nuevamente.');
        case 403:
          throw new Error('No tienes permisos para participar en esta conversación.');
        case 404:
          throw new Error('La conversación o mensaje no fue encontrado.');
        default:
          throw new Error('Error al enviar el mensaje. Intenta nuevamente.');
      }
    }

    throw new Error('Error de conexión. Verifica tu conexión a internet.');
  }
};

// Función para subir adjuntos a un mensaje existente
export const uploadAttachmentsToMessage = async (messageId, attachments = []) => {
  try {
    const formData = new FormData();

    // Añadir archivos adjuntos
    attachments.forEach((file, index) => {
      formData.append('attachments', file);
    });

    const response = await apiClient.post(`/communication/messages/${messageId}/upload_attachments/`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return response;
  } catch (error) {
    if (error.response) {
      const { status, data } = error.response;

      switch (status) {
        case 400:
          if (data.detail && data.detail.includes('size')) {
            throw new Error('El archivo es demasiado grande. El tamaño máximo permitido es 50MB.');
          }
          throw new Error(data.detail || 'Error en los datos enviados');
        case 401:
          throw new Error('No estás autenticado. Por favor, inicia sesión nuevamente.');
        case 403:
          throw new Error('No tienes permisos para añadir adjuntos a este mensaje.');
        case 404:
          throw new Error('El mensaje no fue encontrado.');
        default:
          throw new Error('Error al subir los adjuntos. Intenta nuevamente.');
      }
    }

    throw new Error('Error de conexión. Verifica tu conexión a internet.');
  }
};

// Función para eliminar adjuntos
export const deleteAttachment = async (messageId, attachmentId) => {
  try {
    const response = await apiClient.delete(`/communication/messages/${messageId}/attachments/${attachmentId}/`);
    return response;
  } catch (error) {
    if (error.response) {
      const { status } = error.response;

      switch (status) {
        case 401:
          throw new Error('No estás autenticado.');
        case 403:
          throw new Error('No tienes permisos para eliminar este adjunto.');
        case 404:
          throw new Error('El adjunto o mensaje no fue encontrado.');
        default:
          throw new Error('Error al eliminar el adjunto.');
      }
    }

    throw new Error('Error de conexión.');
  }
};

// Función para descargar adjuntos con manejo de errores
export const downloadAttachment = async (messageId, attachmentId) => {
  try {
    console.log(`Intentando descargar archivo - MessageID: ${messageId}, AttachmentID: ${attachmentId}`);

    // Usar la URL base del apiClient + endpoint de comunicación
    const downloadUrl = `communication/messages/${messageId}/attachments/${attachmentId}/download/`;
    const fullUrl = `${apiClient.defaults.baseURL}${downloadUrl}`;

    console.log('URL de descarga:', fullUrl);

    // Obtener el token de autorización
    const token = localStorage.getItem('markezy_access_token');

    if (token) {
      // Crear la URL con el token como header
      const link = document.createElement('a');
      link.href = fullUrl;
      link.target = '_blank';

      // Intentar descargar usando fetch con headers apropiados
      try {
        const response = await fetch(fullUrl, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        if (response.status === 403) {
          throw new Error('No tienes permisos para descargar este archivo.');
        } else if (response.status === 404) {
          throw new Error('El archivo no fue encontrado.');
        } else if (response.status === 401) {
          throw new Error('Debes iniciar sesión para descargar este archivo.');
        } else if (response.ok) {
          // Si la respuesta es exitosa, crear blob y descargar
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = ''; // El navegador usará el filename del header
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
        } else {
          throw new Error(`Error del servidor: ${response.status}`);
        }
      } catch (fetchError) {
        console.error('Error con fetch, intentando window.open:', fetchError);
        // Fallback: abrir en nueva ventana
        window.open(fullUrl, '_blank');
      }
    } else {
      // Sin token, intentar descarga directa
      console.warn('No hay token disponible, intentando descarga directa');
      window.open(fullUrl, '_blank');
    }

  } catch (error) {
    console.error('Error en downloadAttachment:', error);
    throw error;
  }
};

// Función para descarga directa (como proporcionaste en el ejemplo)
export const directDownloadAttachment = (messageId, attachmentId) => {
  // Direct file download - browser handles the file
  const downloadUrl = `/api/communication/messages/${messageId}/attachments/${attachmentId}/download/`;
  window.open(downloadUrl, '_blank');
};

// Función para verificar permisos de descarga (alternativa que retorna response)
export const getAttachmentDownloadUrl = async (messageId, attachmentId) => {
  try {
    const response = await apiClient.get(`/communication/messages/${messageId}/attachments/${attachmentId}/download/`, {
      responseType: 'blob' // Para archivos binarios
    });

    return response;
  } catch (error) {
    if (error.response) {
      const { status } = error.response;

      switch (status) {
        case 401:
          throw new Error('No estás autenticado.');
        case 403:
          throw new Error('No tienes permisos para descargar este adjunto.');
        case 404:
          throw new Error('El adjunto o mensaje no fue encontrado.');
        default:
          throw new Error('Error al descargar el adjunto.');
      }
    }

    throw new Error('Error de conexión.');
  }
};

// Función para obtener una URL de vista previa para imágenes adjuntas
export const getAttachmentPreviewUrl = async (messageId, attachmentId) => {
  try {
    const response = await apiClient.get(
      `/communication/messages/${messageId}/attachments/${attachmentId}/preview/`,
      {
        responseType: 'blob',
      }
    );

    return window.URL.createObjectURL(response.data);
  } catch (error) {
    if (error.response) {
      const { status, data } = error.response;

      switch (status) {
        case 400:
          throw new Error(data?.detail || 'Este archivo no admite vista previa.');
        case 401:
          throw new Error('No estás autenticado.');
        case 403:
          throw new Error('No tienes permisos para ver la vista previa de este adjunto.');
        case 404:
          throw new Error('El adjunto o mensaje no fue encontrado.');
        default:
          throw new Error('Error al cargar la vista previa del adjunto.');
      }
    }

    throw new Error('Error de conexión.');
  }
};

// La imagen del ticket no es pública: se pide con el token y se devuelve como blob URL
export const getTicketImageUrl = async (ticketId) => {
  const response = await apiClient.get(`/communication/tickets/${ticketId}/image/`, {
    responseType: 'blob',
  });
  return window.URL.createObjectURL(response.data);
};

export default apiClient;
